import { registerEdgeType } from '../services/EdgeTypeRegistry';
import { straightEdgeType } from './straightEdgeType';
import type { EdgeTypeDefinition, EdgeBoundsContext, EdgeLineBounds, TextAnchor } from '../types/EdgeTypes';

const S_CURVE_OFFSET_RATIO = 0.15;

interface ControlPoints {
  cp1X: number;
  cp1Y: number;
  cp2X: number;
  cp2Y: number;
}

function createPath(
  ctx: CanvasRenderingContext2D,
  startX: number,
  startY: number,
  endX: number,
  endY: number
): void {
  // Template preview fallback: draw horizontal line
  straightEdgeType.createPath(ctx, startX, startY, endX, endY);
}

function _getControlPoints(fromX: number, fromY: number, toX: number, toY: number): ControlPoints {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector
  const perpX = -dy / len;
  const perpY = dx / len;
  const offset = len * S_CURVE_OFFSET_RATIO;

  return {
    // CP1 at 1/3 along, offset one direction
    cp1X: fromX + dx / 3 + perpX * offset,
    cp1Y: fromY + dy / 3 + perpY * offset,
    // CP2 at 2/3 along, offset opposite direction
    cp2X: fromX + 2 * dx / 3 - perpX * offset,
    cp2Y: fromY + 2 * dy / 3 - perpY * offset,
  };
}

function createWorldPath(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): Path2D {
  const { cp1X, cp1Y, cp2X, cp2Y } = _getControlPoints(fromX, fromY, toX, toY);

  const path = new Path2D();
  path.moveTo(fromX, fromY);
  path.bezierCurveTo(cp1X, cp1Y, cp2X, cp2Y, toX, toY);

  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.bezierCurveTo(cp1X, cp1Y, cp2X, cp2Y, toX, toY);

  return path;
}

function getTangentAtStart(fromX: number, fromY: number, toX: number, toY: number): number {
  // Tangent at t=0 of cubic bezier points from start toward CP1
  const { cp1X, cp1Y } = _getControlPoints(fromX, fromY, toX, toY);
  return Math.atan2(cp1Y - fromY, cp1X - fromX);
}

function getTangentAtEnd(fromX: number, fromY: number, toX: number, toY: number): number {
  // Tangent at t=1 of cubic bezier points from CP2 toward end
  const { cp2X, cp2Y } = _getControlPoints(fromX, fromY, toX, toY);
  return Math.atan2(toY - cp2Y, toX - cp2X);
}

function getTextAnchor(fromX: number, fromY: number, toX: number, toY: number): TextAnchor {
  // Evaluate cubic bezier at t=0.5
  const { cp1X, cp1Y, cp2X, cp2Y } = _getControlPoints(fromX, fromY, toX, toY);
  const t = 0.5;
  const mt = 1 - t;

  const x = mt * mt * mt * fromX + 3 * mt * mt * t * cp1X + 3 * mt * t * t * cp2X + t * t * t * toX;
  const y = mt * mt * mt * fromY + 3 * mt * mt * t * cp1Y + 3 * mt * t * t * cp2Y + t * t * t * toY;

  // Tangent at t=0.5: derivative of cubic bezier
  const tx = 3 * mt * mt * (cp1X - fromX) + 6 * mt * t * (cp2X - cp1X) + 3 * t * t * (toX - cp2X);
  const ty = 3 * mt * mt * (cp1Y - fromY) + 6 * mt * t * (cp2Y - cp1Y) + 3 * t * t * (toY - cp2Y);
  const angle = Math.atan2(ty, tx);

  return { x, y, angle };
}

function calculateLineBounds(context: EdgeBoundsContext): EdgeLineBounds {
  return straightEdgeType.calculateLineBounds(context);
}

const sCurvedEdgeType: EdgeTypeDefinition = {
  value: 's_curved',
  worldSpace: true,
  createPath,
  calculateLineBounds,
  createWorldPath,
  getTangentAtStart,
  getTangentAtEnd,
  getTextAnchor,
};

registerEdgeType(sCurvedEdgeType);

export { sCurvedEdgeType };
